import {
  UserPermission,
  UserPermissions,
} from "../plugins/Cruxtify/UserPermission";

import { ErCrudModel } from "./ErCrudModel";

export class Subscription extends ErCrudModel {
  protected static _typeLabel = "Subscription";
  protected static _typeLabelPlural = "Subscriptions";
  protected static asProperty = "subscription";
  public static api = {
    path: "subscriptions",
    fullCache: true,
  };
  protected static routeBase = "subscriptions";
  protected static userPermissions = new UserPermissions(UserPermission.Read);

  // Starts the one-plan Checkout Session (SubscriptionController::checkout).
  // Returns { id: <Checkout Session id> } for PlanStatusCard's Stripe redirect.
  // `next` (e.g. from the membership offer page mid-registration) rides
  // through Stripe and back via the server's own `next` handling, and also
  // drives where a declined checkout returns. `successNext` drives only
  // where a *completed* checkout lands, falling back server-side to `next`
  // when absent. Both are omitted entirely, not sent as `undefined`, so
  // PlanStatusCard's existing no-arg call is unchanged.
  public static checkout(next?: string, successNext?: string) {
    const body: Record<string, string> = {};
    if (next !== undefined) body.next = next;
    if (successNext !== undefined) body.success_next = successNext;
    return this.$nuxt.$api.POST("subscription/checkout", body);
  }

  protected userIdField = ["user"];

  protected static propertyDefinitions = [
    {
      type: "NumberProperty",
      opts: {
        name: "id",
        label: "ID",
        userPermissions: UserPermission.Hidden,
      },
    },
    {
      type: "DateTimeProperty",
      opts: {
        name: "created_at",
        label: "Entry Created At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: "DateTimeProperty",
      opts: {
        name: "updated_at",
        label: "Entry Updated At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: "StringProperty",
      opts: {
        name: "type",
        label: "Type",
      },
    },
    {
      type: "StringProperty",
      opts: {
        name: "stripe_id",
        label: "Stripe ID",
      },
    },
    {
      type: "StringProperty",
      opts: {
        name: "stripe_status",
        label: "Stripe Status",
      },
    },
    {
      type: "NumberProperty",
      opts: {
        name: "stripe_price",
        label: "Stripe Price",
      },
    },
    {
      type: "NumberProperty",
      opts: {
        name: "quantity",
        label: "Quantity",
      },
    },
    {
      type: "DateTimeProperty",
      opts: {
        name: "trial_ends_at",
        label: "Trial Ends At",
      },
    },
    {
      type: "DateTimeProperty",
      opts: {
        name: "ends_at",
        label: "Ends At",
      },
    },
    {
      type: "RelationshipProperty",
      opts: {
        name: "user",
        label: "User",
        relatedModel: "User",
        foreignProperty: "subscription",
      },
    },
    {
      type: "RelationshipPropertyMany",
      opts: {
        name: "subscription_items",
        label: "Subscription Items",
        relatedModel: "SubscriptionItem",
        foreignProperty: "subscription",
      },
    },
  ];

  protected static fieldDefinitions = [
    { type: "NumberField", opts: { property: "id" } },
    {
      type: "DateTimeField",
      opts: { property: "created_at" },
    },
    { type: "DateTimeField", opts: { property: "updated_at" } },
    {
      type: "StringField",
      opts: { property: "stripe_id", isPrimaryLabelField: true },
    },
    {
      type: "StringField",
      opts: { property: "stripe_status" },
    },
    {
      type: "NumberField",
      opts: { property: "stripe_price" },
    },
    {
      type: "NumberField",
      opts: { property: "quantity" },
    },
    {
      type: "DateTimeField",
      opts: { property: "trial_ends_at" },
    },
    {
      type: "DateTimeField",
      opts: { property: "ends_at" },
    },
    {
      type: "RelationshipField",
      opts: {
        property: "user",
      },
    },
    {
      type: "RelationshipFieldMany",
      opts: {
        property: "subscription_items",
        viewComponentName: "form-relationship-field-many-table",
        formComponentName: "form-relationship-field-many-table",
      },
    },
  ];

  protected static collectionLayoutDefinitions = [
    {
      id: "table",
      columnFields: ["user", "stripe_id", "stripe_status", "stripe_price"],
      enableDynamicFilters: false,
      useModal: true,
      enableSearch: false,
      defaultSort: [
        {
          field: "updated_at",
          order: "desc",
        },
      ],
    },
  ];
}
